import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from 'src/app/helpers/base.service';
import { StorageService } from 'src/app/helpers/storage.service';
import { environment } from 'src/environments/environment.development';

export interface InvoicingCustomerLoginResult {
  token: string;
  customerName: string;
  email: string;
  customerId: string;
}

export interface InvoicingCustomerLoginResponse {
  statusCode: number;
  isSuccess: boolean;
  message: string;
  errors: any;
  result: InvoicingCustomerLoginResult;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerPortalService extends BaseService {
  private readonly _loginUrl = `${environment.invoices_base_url}/api/CustomerAuth/Login`;
  private readonly _getAgreementsUrl = `${environment.invoices_base_url}/api/Agreement/GetCustomerAgreements`;
  private readonly _tokenKey = 'invoicing_customer_token';
  private readonly _userKey = 'invoicing_customer_user';

  constructor(
    private _storageServ: StorageService,
    private _router: Router,
  ) {
    super();
  }

  login(payload: any): Observable<InvoicingCustomerLoginResponse> {
    return this._http
      .post<InvoicingCustomerLoginResponse>(this._loginUrl, payload)
      .pipe(
        map((response) => {
          if (response.isSuccess && response.result) {
            this.setSession(response.result);
          }
          return response;
        }),
      );
  }

  forgotPassword(email: string): Observable<any> {
    const params = new HttpParams().set('email', email);
    return this._http.post<any>(
      `${environment.invoices_base_url}/api/CustomerAuth/ForgotPassword`,
      {},
      { params }
    );
  }

  resetPassword(payload: { token: string; newPassword: string }): Observable<any> {
    return this._http.post<any>(
      `${environment.invoices_base_url}/api/CustomerAuth/ResetPassword`,
      payload
    );
  }

  getAgreements(payload: any): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });
    return this._http.post<any>(this._getAgreementsUrl, payload, { headers });
  }

  getInvoices(payload: any): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });
    return this._http.post<any>(
      `${environment.invoices_base_url}/api/Invoice/GetCustomerInvoices`,
      payload,
      { headers }
    );
  }

  getAgreementById(id: string): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });
    return this._http.get<any>(
      `${environment.invoices_base_url}/api/Agreement/GetById/${id}`,
      { headers }
    );
  }

  generateAgreementPdf(agreementId: string): Observable<Blob> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });
    const params = new HttpParams().set('agreementId', agreementId);
    return this._http.get(
      `${environment.invoices_base_url}/api/EmailSender/GetAgreementPdf`,
      { headers, params, responseType: 'blob' }
    );
  }

  signAgreement(payload: any): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });
    return this._http.post<any>(
      `${environment.invoices_base_url}/api/Signature/SignAgreement`,
      payload,
      { headers },
    );
  }

  setSession(result: InvoicingCustomerLoginResult): void {
    this._storageServ.setItem(this._tokenKey, result.token);
    this._storageServ.setItem(this._userKey, result);
    this._dataServ.setRole('customer');
    this._dataServ.setCustomer({
      customerId: result.customerId,
      customerName: result.customerName,
      email: result.email,
      role: 'customer',
    });
  }

  getToken(): string | null {
    return this._storageServ.getItem(this._tokenKey);
  }

  getCustomerInfo(): InvoicingCustomerLoginResult | null {
    return this._storageServ.getItem(this._userKey);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  logout(): void {
    const token = this.getToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      isCustomerPortal: 'true',
    });

    this._storageServ.removeItem(this._tokenKey);
    this._storageServ.removeItem(this._userKey);
    this._dataServ.clear();
    this._storageServ.removeItem(this._tokenKey);
    this._storageServ.removeItem(this._userKey);

    const logoutUrl = `${environment.invoices_base_url}/api/CustomerAuth/Logout`;
    this._http.post(logoutUrl, {}, { headers }).subscribe({
      next: () => {},
      error: (err) => {
        console.error('Logout API failed', err);
      },
    });

    window.location.href = '/invoicing/customer-portal/login';
  }
}
